import {socialFallbackFailurePath} from '@/lib/socialAuth.mjs';
// src/Routes.jsx
import React, { Suspense, lazy, useEffect } from "react";
import { Routes, Route, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import ProtectedRoute from "@/components/auth/ProtectedRoute.jsx";
import PharmacyProtectedRoute from "@/components/auth/PharmacyProtectedRoute.jsx";
import { BasicDialogFrame } from "@/components/ui/BasicDialog.jsx";
import ForgotPassword from "@/pages/login/ForgotPassword.jsx";
import ResetPassword from "@/pages/reset-password/index.jsx";
import RouteFallback from "@/sot/primitives/RouteFallback.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import AppShell from "@/components/layout/AppShell.jsx";
import ChooseRole from "@/pages/auth/ChooseRole.jsx";
import { readLastRole, writeLastRole } from "@/lib/roleCache";
import { shouldShowSettingsWelcome } from "@/lib/settingsWelcomeState.js";
import { writeUserActivityEvent } from "@/services/userTruthTracking.js";
import { hasPasswordConfigured, shouldOfferCreatePassword } from "@/features/security/accountSecurityState.js";
import { onConsentChanged, readConsentState } from "@/lib/consent/getConsent.js";
import { selectControlledFirstUseRoute } from "@/lib/roleResolution.mjs";
import { resolveStagingFirstUseDisposition } from "@/services/stagingAccessService.js";
import StagingSignupPage from "@/pages/signup/StagingSignupPage.jsx";

import PatientSettingsRoutes from "@/pages/profile-settings/PatientSettingsRoutes.jsx";
import EhrPasswordResetConfirm from "@/pages/profile-settings/patient/EhrPasswordResetConfirm.jsx";
import ChangePassword from "@/pages/profile-settings/patient/ChangePassword.jsx";

import OnboardingRouter from "@/pages/onboarding/OnboardingRouter.jsx";

// Profiles (non-lazy, blueprint-style)
import DoctorProfile from "@/pages/profile/doctor/DoctorProfile.jsx";
import PatientProfile from "@/pages/profile/patient/PatientProfile.jsx";
import PatientPhotos from "@/pages/patient/PatientPhotos.jsx";

import HospitalsPage from "@/pages/hospitals/index.jsx";
import PharmaciesPage from "@/pages/pharmacies/index.jsx";
import AmbulancesPage from "@/pages/ambulances/index.jsx";
import PharmacyWorkspace from "@/pages/pharmacy/PharmacyWorkspace.jsx";
import PharmacyMemberCreatePage from "@/pages/pharmacy/PharmacyMemberCreatePage.jsx";
import PharmacyInventoryItemCreatePage from "@/pages/pharmacy/PharmacyInventoryItemCreatePage.jsx";
import PharmacyEntry from "@/pages/pharmacy/PharmacyEntry.jsx";
import PharmacyInviteLanding from "@/pages/pharmacy/PharmacyInviteLanding.jsx";
import PracticeTeamInviteLanding from "@/pages/profile-settings/doctor/PracticeTeamInviteLanding.jsx";
import PracticeTeamInviteAccept from "@/pages/profile-settings/doctor/PracticeTeamInviteAccept.jsx";
import PracticeTeamWelcome from "@/pages/profile-settings/doctor/PracticeTeamWelcome.jsx";
import PracticeTeamTasks from "@/pages/profile-settings/doctor/PracticeTeamTasks.jsx";
import PracticeTeamAppointments from "@/pages/profile-settings/doctor/PracticeTeamAppointments.jsx";
import PracticeTeamMessages from "@/pages/profile-settings/doctor/PracticeTeamMessages.jsx";
import PracticeTeamPatients from "@/pages/profile-settings/doctor/PracticeTeamPatients.jsx";
import PracticeTeamAccount from "@/pages/profile-settings/doctor/PracticeTeamAccount.jsx";
import PracticeTeamAppearance from "@/pages/profile-settings/doctor/PracticeTeamAppearance.jsx";
import { usePracticeTeamAccess } from "@/pages/profile-settings/doctor/practiceTeamMemberAccess.js";
import PharmacyOrderDetails from "@/pages/pharmacy/PharmacyOrderDetails.jsx";
import PharmacyAccount from "@/pages/pharmacy/PharmacyAccount.jsx";
import PharmacyAppearance from "@/pages/pharmacy/PharmacyAppearance.jsx";
import PharmacyDesktopHeader from "@/components/nav/PharmacyDesktopHeader.jsx";
import {
  getActivePharmacyMemberAccess,
  PHARMACY_NOTIFICATIONS_CHANGED_EVENT,
  readPharmacyNotifications,
  resolvePersistedPharmacyAccess,
} from "@/services/pharmacyLaneService.js";

import PatientHome from "@/pages/home/PatientHome.jsx";
import DoctorHome from "@/pages/home/DoctorHome.jsx";
import DoctorDashboardPage from "@/pages/doctors/dashboard/index.jsx";
import InstitutionsPage from "@/pages/institutions/InstitutionsPage.jsx";
import InstitutionEvaluationAccept from "@/pages/institutions/InstitutionEvaluationAccept.jsx";
import InstitutionClinicianInvitationReview from "@/pages/institutions/InstitutionClinicianInvitationReview.jsx";
import InstitutionScopedInvitationAccept from "@/pages/institutions/InstitutionScopedInvitationAccept.jsx";
import DoctorRecentPrescriptionsPage from "@/pages/doctors/prescriptions/index.jsx";
import RegionalHealth from "@/pages/regional-health/RegionalHealth.jsx";
import MessagesPage from "@/pages/messages/index.jsx";
import ConversationPage from "@/pages/messages/Conversation.jsx";
import HelpCentre from "@/pages/help/index.jsx";
import HelpSupport from "@/pages/help-support/index.jsx";
import KnowledgeBase from "@/pages/knowledge-base/index.jsx";
import KnowledgeBaseArticleView from "@/pages/knowledge-base/components/ArticleView.jsx";
import CommunicationPreferencesPage from "@/pages/communications/CommunicationPreferencesPage.jsx";
import CommunicationUnsubscribePage from "@/pages/communications/CommunicationUnsubscribePage.jsx";
import PrivacyPolicyPage from "@/pages/legal/PrivacyPolicyPage.jsx";
import TermsConditionsPage from "@/pages/legal/TermsConditionsPage.jsx";
import DataDeletionPage from "@/pages/legal/DataDeletionPage.jsx";
import AdminTeamAccept from "@/pages/admin/AdminTeamAccept.jsx";

function PracticeTeamWorkspaceRoute({ children, requiredSectionId = "" }) {
  const location = useLocation();
  const { user } = useAuth() || {};
  const { resolving, allowed } = usePracticeTeamAccess({ requiredSectionId });

  if (resolving) return <RouteFallback label="Checking Practice Team access..." />;

  if (!user?.id) {
    const destination = `${location.pathname || ""}${location.search || ""}${location.hash || ""}`;
    const params = new URLSearchParams();
    if (destination.startsWith("/") && !destination.startsWith("//")) params.set("next", destination);
    return <Navigate to={params.toString() ? `/login?${params.toString()}` : "/login"} replace />;
  }

  if (!allowed) {
    return (
      <div className="auth-bg min-h-[100dvh] grid place-items-center px-4 text-center">
        <div className="max-w-[380px] space-y-2">
          <img src="/assets/images/afrimed_logo_stack.svg" alt="AfriMed" className="mx-auto mb-4 w-28" />
          <h1 className="text-title-large text-on-surface">Practice Team access unavailable</h1>
          <p className="text-body-medium text-on-surface-var">
            This account does not have active Practice Team membership for this workspace.
          </p>
        </div>
      </div>
    );
  }

  return children;
}

function PharmacyMessagesShell({ children }) {
  const [notifications, setNotifications] = React.useState(() => readPharmacyNotifications());

  React.useEffect(() => {
    const refreshNotifications = () => setNotifications(readPharmacyNotifications());
    refreshNotifications();
    window.addEventListener("storage", refreshNotifications);
    window.addEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    return () => {
      window.removeEventListener("storage", refreshNotifications);
      window.removeEventListener(PHARMACY_NOTIFICATIONS_CHANGED_EVENT, refreshNotifications);
    };
  }, []);

  return (
    <div className="flex h-[100dvh] min-h-0 flex-col overflow-hidden bg-[rgb(var(--clr-surface))]">
      <div className="hidden shrink-0 md:block">
        <PharmacyDesktopHeader section="messages" notifications={notifications} />
      </div>
      <main
        data-testid="pharmacy-messages-shell-rail"
        className="afrimed-rail afrimed-rail--comfortable flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        {children}
      </main>
    </div>
  );
}

// Appointments directory pages
import AppointmentsLayout from "@/pages/appointments/AppointmentsLayout.jsx";
import AppointmentsIndex from "@/pages/appointments/index.jsx";
import AppointmentsDoctorsPage from "@/pages/appointments/DoctorsPage.jsx";
import AppointmentDoctorPreview from "@/pages/appointments/doctor/AppointmentDoctorPreview.jsx";
import AppointmentBook from "@/pages/appointments/book/AppointmentBook.jsx";
import AppointmentConfirm from "@/pages/appointments/confirm/AppointmentConfirm.jsx";
import AppointmentPay from "@/pages/appointments/pay/AppointmentPay.jsx";
import WorldlinePaymentReturn from "@/pages/appointments/pay/WorldlinePaymentReturn.jsx";
import SponsorWorldlinePayment from "@/pages/pay/SponsorWorldlinePayment.jsx";
import AppointmentSuccess from "@/pages/appointments/success/AppointmentSuccess.jsx";
import AppointmentDetails from "@/pages/appointments/details/AppointmentDetails.jsx";
import EditAppointment from "@/pages/appointments/edit/EditAppointment.jsx";
import AppointmentEditMenu from "@/pages/appointments/edit/AppointmentEditMenu.jsx";
import EditAppointmentLocation from "@/pages/appointments/edit/EditAppointmentLocation.jsx";
import AppointmentsNearby from "@/pages/appointments/AppointmentsNearby.jsx";
import PastAppointments from "@/pages/appointments/past/PastAppointments.jsx";
import PastAppointmentDetails from "@/pages/appointments/past/PastAppointmentDetails.jsx";
import AppointmentCall from "@/pages/appointments/call/AppointmentCall.jsx";
import AppointmentVideo from "@/pages/appointments/call/AppointmentVideo.jsx";
import AdultDependantBrowserJoin from "@/pages/appointments/join/AdultDependantBrowserJoin.jsx";
import AppointmentSummary from "@/pages/appointments/summary/AppointmentSummary.jsx";
import PatientPrescriptionsIndex from "@/pages/prescriptions/PatientPrescriptionsIndex.jsx";
import PatientPrescriptionDetails from "@/pages/prescriptions/PatientPrescriptionDetails.jsx";
import PatientPrescriptionPayment from "@/pages/prescriptions/PatientPrescriptionPayment.jsx";
import VerifyPrescription from "@/features/verify/VerifyPrescription.jsx";
import PharmacyScanPrescription from "@/pages/pharmacies/PharmacyScanPrescription.jsx";
import DoctorPrescriptionWizard from "@/pages/appointments/prescriptions/DoctorPrescriptionWizard.jsx";
import { AppointmentsFiltersProvider } from "@/pages/appointments/AppointmentsFiltersContext.jsx";
import AppointmentsHospitalsPage from "@/pages/appointments/HospitalsPage.jsx";
import AppointmentsPharmaciesPage from "@/pages/appointments/PharmaciesPage.jsx";
import DoctorReferralCreate from "@/pages/referrals/DoctorReferralCreate.jsx";
import PatientReferralDetails from "@/pages/referrals/PatientReferralDetails.jsx";
import ReferralDocumentRedirect from "@/pages/referrals/ReferralDocumentRedirect.jsx";
import DoctorReferralNetwork from "@/pages/referrals/DoctorReferralNetwork.jsx";

import DoctorAppointmentsPage from "@/pages/doctors/appointments/index.jsx";
import DoctorAppointmentDetails from "@/pages/doctors/appointments/details/DoctorAppointmentDetails.jsx";
import ClinicianAppointmentPatientProfile from "@/pages/doctors/appointments/details/ClinicianAppointmentPatientProfile.jsx";
import DoctorVitalRepBeta from "@/pages/doctors/appointments/DoctorVitalRepBeta.jsx";
import DoctorDiagnosisNotes from "@/pages/doctors/appointments/DoctorDiagnosisNotes.jsx";
import DoctorEhrRequest from "@/pages/doctors/appointments/DoctorEhrRequest.jsx";
import DoctorPatientEhr from "@/pages/doctors/patients/DoctorPatientEhr.jsx";
import DoctorPastAppointments from "@/pages/doctors/patients/DoctorPastAppointments.jsx";
import DoctorPastAppointmentDetails from "@/pages/doctors/patients/DoctorPastAppointmentDetails.jsx";
import DoctorPatientPhotos from "@/pages/doctors/appointments/DoctorPatientPhotos.jsx";


// Lazy pages
const LoginPage = lazy(() => import("./pages/login"));
const InviteSetupPage = lazy(() => import("@/pages/invite/InviteSetup.jsx"));
const PatientOnboarding = lazy(() =>
  import("./pages/onboarding/patient/PatientOnboarding")
);
const DoctorOnboarding = lazy(() =>
  import("./pages/onboarding/doctor/DoctorOnboarding")
);
const AuthCallback = lazy(() => import("@/pages/auth/AuthCallback.jsx"));
const AdminKyc = lazy(() => import("@/pages/admin/KycReview.jsx"));
const AdminKpi = lazy(() => import("@/pages/admin/KpiDashboard.jsx"));
const AdminSignIn = lazy(() => import("@/pages/admin/AdminSignIn.jsx"));
const AdminInviteFunnel = lazy(() => import("@/pages/admin/InviteFunnel.jsx"));
const AdminAccount = lazy(() => import("@/pages/admin/AdminAccount.jsx"));
const AdminFeedback = lazy(() => import("@/pages/admin/AdminFeedback.jsx"));
const AdminSupport = lazy(() => import("@/pages/admin/AdminSupport.jsx"));
const RegionalHealthLegacyRedirect = lazy(() =>
  import("@/pages/institutions/RegionalHealthLegacyRedirect.jsx")
);
const AdminTeam = lazy(() => import("@/pages/admin/AdminTeam.jsx"));
const AdminUsers = lazy(() => import("@/pages/admin/AdminUsers.jsx"));
const FeedbackPortal = lazy(() => import("@/pages/feedback/FeedbackPortal.jsx"));
const FeedbackThanks = lazy(() =>
  import("@/pages/feedback/FeedbackPortal.jsx").then((module) => ({
    default: module.FeedbackThanks,
  })),
);
const ProfileSettings = lazy(() => import("@/pages/profile-settings/index.jsx"));
const NotificationsOpen = lazy(() =>
  import("@/pages/profile-settings/patient/NotificationsOpen.jsx")
);
const DoctorSettingsRoutes = lazy(() =>
  import("@/pages/profile-settings/doctor/SettingsRoutesDoctor.jsx")
);
const ResearchPublicHealthSettings = lazy(() =>
  import("@/pages/profile-settings/research/ResearchPublicHealthSettings.jsx")
);
const DoctorNotificationsOpen = lazy(() =>
  import("@/pages/profile-settings/doctor/NotificationsOpen.jsx")
);
const DoctorNotifications = lazy(() =>
  import("@/pages/profile-settings/doctor/Notifications.jsx")
);
const StripeConnectLaunch = lazy(() =>
  import("@/pages/profile-settings/doctor/StripeConnectLaunch.jsx")
);

const AUTH_PASSWORD_PRIMARY_ENABLED =
  String(import.meta.env.VITE_AUTH_PASSWORD_PRIMARY_ENABLED || "").trim().toLowerCase() === "true";
const CREATE_PASSWORD_PROMPT_DELAY_MS = 900;

// Shared chrome for authed areas
function AppLayout() {
  return (
    <AppShell>
      <RoleTracker />
      <ActivityTracker />
      <Outlet />
    </AppShell>
  );
}

function normalizeRole(value) {
  const role = String(value || "").toLowerCase().trim();
  return role === "doctor" ||
    role === "patient" ||
    role === "admin" ||
    role === "support" ||
    role === "kyc_reviewer" ||
    role === "pharmacy" ||
    role === "practice_team" ||
    role === "super_admin" ||
    role === "moh_admin" ||
    role === "institution_admin" ||
    role === "facility_admin"
    ? role
    : "";
}

function resolveRole(auth, { allowStorage = true } = {}) {
  const user = auth?.user || null;
  const profile = auth?.profile || null;

  const roleFromProfile = normalizeRole(profile?.role);
  if (roleFromProfile) return roleFromProfile;

  const hasDoctor = !!profile?.hasDoctor;
  const hasPatient = !!profile?.hasPatient;

  if (hasDoctor && !hasPatient) return "doctor";
  if (!hasDoctor && hasPatient) return "patient";

  if (profile && profile.role_selected === false && !hasDoctor && !hasPatient) {
    return "";
  }

  if (allowStorage) {
    const savedRole = readLastRole(user?.id);
    if (savedRole) return savedRole;
  }

  // Last-resort fallback only when DB/profile shape can't resolve role.
  const roleFromMeta =
    normalizeRole(auth?.role) ||
    normalizeRole(user?.role) ||
    normalizeRole(user?.user_metadata?.role) ||
    normalizeRole(user?.user_metadata?.primary_role) ||
    normalizeRole(user?.app_metadata?.role);

  if (roleFromMeta) return roleFromMeta;

  return "";
}

function isAuthTruthResolving(auth) {
  if (Boolean(auth?.loading) && !auth?.user?.id) return true;
  if (auth?.profileResolved === false && (!auth?.user?.id || !auth?.profile)) return true;
  return false;
}

function isIncompletePharmacyProfile(auth) {
  const role = resolveRole(auth);
  return role === "pharmacy" && auth?.profile?.onboarding_completed !== true;
}

function LegacyPreferencesRedirect({ role }) {
  const { pathname, search, hash } = useLocation();
  const safeRole = role === "doctor" ? "doctor" : "patient";
  const tail = pathname.replace(new RegExp(`^/${safeRole}/preferences/?`), "");
  const target = `/settings/${safeRole}/preferences${tail ? `/${tail}` : ""}${search}${hash}`;
  return <Navigate to={target} replace />;
}

// Role-aware settings gate (redirect-only, scope-pure)
function SettingsGate() {
  const auth = useAuth() || {};
  const effectiveRole = resolveRole(auth);

  if (isAuthTruthResolving(auth)) {
    return <RouteFallback label="Loading AfriMed..." />;
  }

  if (!effectiveRole) {
    if (auth?.profile?.hasDoctor && auth?.profile?.hasPatient) {
      return <Navigate to="/choose-role" replace />;
    }
    return <Navigate to="/home" replace />;
  }

  if (isIncompletePharmacyProfile(auth)) {
    return <Navigate to="/onboarding/pharmacy" replace />;
  }

  const target =
    effectiveRole === "doctor"
      ? "/settings/doctor"
      : effectiveRole === "patient"
        ? "/settings/patient"
        : effectiveRole === "pharmacy"
          ? "/pharmacy/settings"
          : "/admin/kpi";

  return <Navigate to={target} replace />;
}

function SettingsScopedRedirect() {
  const auth = useAuth() || {};
  const { pathname } = useLocation();
  const effectiveRole = resolveRole(auth);

  if (isAuthTruthResolving(auth)) {
    return <RouteFallback label="Loading AfriMed..." />;
  }

  if (!effectiveRole) {
    if (auth?.profile?.hasDoctor && auth?.profile?.hasPatient) {
      return <Navigate to="/choose-role" replace />;
    }
    return <Navigate to="/home" replace />;
  }

  if (isIncompletePharmacyProfile(auth)) {
    return <Navigate to="/onboarding/pharmacy" replace />;
  }

  const base =
    effectiveRole === "doctor"
      ? "/settings/doctor"
      : effectiveRole === "patient"
        ? "/settings/patient"
        : effectiveRole === "pharmacy"
          ? "/pharmacy/settings"
          : "/admin/kpi";

  // Preserve anything after "/settings/"
  const tail = pathname.replace(/^\/settings\/?/, ""); // "login-security", "preferences/appearance", etc.

  const target = tail ? `${base}/${tail}` : base;
  return <Navigate to={target} replace />;
}

// Preserve the historical short institution URL used by existing navigation
// and bookmarks. The destination remains inside this router and is still
// protected by the canonical institution route guard.
function LegacyInstitutesRedirect() {
  const location = useLocation();
  const suffix = String(location.pathname || "").replace(/^\/institutes(?=\/|$)/, "");
  const pathname = `/institutions${suffix}`.replace(/\/{2,}/g, "/");
  return <Navigate to={{ pathname, search: location.search }} replace />;
}

function LegacyInstitutionFollowupRedirect() {
  const location = useLocation();
  return <Navigate to={{ pathname: "/institutions/follow-ups", search: location.search }} replace />;
}

/**
 * Router-level role lock.
 * Use this when a route should ONLY be accessible by a doctor/patient.
 */
function RequireRole({ allow = ["patient", "doctor"], children }) {
  const auth = useAuth() || {};
  const role = resolveRole(auth);

  if (isAuthTruthResolving(auth)) {
    return <RouteFallback />;
  }

  if (!role) {
    if (auth?.profile?.hasDoctor && auth?.profile?.hasPatient) {
      return <Navigate to="/choose-role" replace />;
    }
    return <Navigate to="/home" replace />;
  }

  // If role is known, enforce it.
  if (!allow.includes(role)) {
    // send them to their correct home (safe fallback)
    const target =
      role === "doctor"
        ? "/home/doctor"
        : role === "patient"
            ? "/home/patient"
            : role === "pharmacy"
              ? "/pharmacy/dashboard"
              : role === "practice_team"
                ? "/practice-team/welcome"
            : role === "super_admin" ||
                role === "moh_admin" ||
                role === "institution_admin" ||
                role === "facility_admin"
              ? "/institutions/home"
            : "/admin/kpi";
    return <Navigate to={target} replace />;
  }

  return children;
}

function hasExplicitPublicDoctorTarget(location) {
  const params = new URLSearchParams(location?.search || "");
  if (String(params.get("doctorId") || "").trim()) return true;
  const state = location?.state || {};
  return Boolean(
    state.fromPublic ||
      state.from === "patient" ||
      state.doctorUserId ||
      state.doctorId ||
      state.doctor ||
      state.doctorProfile,
  );
}

/**
 * Self-profile routes are role-owned. The explicit public-doctor route is the
 * only exception: it supplies a separate target and DoctorProfile renders it
 * as read-only. This guard runs before either profile component reads auth
 * data, preventing a hydrated patient object from becoming a doctor surface
 * (and vice versa) during route transitions.
 */
function ProfileRoleBoundary({ requestedRole, children }) {
  const auth = useAuth() || {};
  const location = useLocation();
  const role = resolveRole(auth);

  if (isAuthTruthResolving(auth)) return <RouteFallback />;

  if (requestedRole === "doctor" && hasExplicitPublicDoctorTarget(location)) {
    return children;
  }

  if (role === requestedRole) return children;

  if (role === "doctor") return <Navigate to="/doctor/profile" replace />;
  if (role === "patient") return <Navigate to="/patient/profile" replace />;
  return <Navigate to="/home" replace />;
}

/**
 * /home entrypoint that ALWAYS resolves to the correct home.
 * - Prevents "race conditions" by waiting for the Auth profile to load.
 * - Prioritizes verified DB role over LocalStorage to stop "Role Ghosting".
 * - Persists the resolved role for stable navigation on neutral routes.
 */
function HomeGate() {
  // We pull 'loading' and 'profile' from AuthContext (via App.jsx fetch)
  const auth = useAuth() || {};
  const { profile } = auth;
  const finalRole = resolveRole(auth);
  const settingsWelcomePending =
    (finalRole === "doctor" || finalRole === "patient") &&
    shouldShowSettingsWelcome({
      userId: auth?.user?.id,
      role: finalRole,
      profile,
    });
  const [controlledFirstUse, setControlledFirstUse] = React.useState({
    resolved: false,
    controlled: false,
    journey: "",
    role: "",
    stage: "",
    onboardingCompleted: false,
    institutionMembership: { assigned: false, destination: "" },
  });

  React.useEffect(() => {
    let alive = true;
    const needsFirstUseCheck = Boolean(auth?.user?.id && !profile?.onboarding_completed);
    if (!needsFirstUseCheck) {
      setControlledFirstUse({ resolved: true, controlled: false, journey: "", role: "", stage: "", onboardingCompleted: false, institutionMembership: { assigned: false, destination: "" } });
      return () => { alive = false; };
    }

    setControlledFirstUse((current) => ({ ...current, resolved: false }));
    resolveStagingFirstUseDisposition()
      .then((result) => {
        if (!alive) return;
        setControlledFirstUse({
          resolved: true,
          controlled: result?.controlled === true,
          journey: String(result?.journey || "").trim(),
          role: String(result?.role || "").trim(),
          stage: String(result?.stage || "").trim(),
          onboardingCompleted: result?.onboardingCompleted === true,
          institutionMembership: result?.institutionMembership || { assigned: false, destination: "" },
        });
      })
      .catch(() => {
        if (!alive) return;
        setControlledFirstUse({ resolved: true, controlled: false, journey: "", role: "", stage: "", onboardingCompleted: false, institutionMembership: { assigned: false, destination: "" } });
      });
    return () => { alive = false; };
  }, [auth?.user?.id, profile?.onboarding_completed]);

  // 3. PERSIST: Sync the confirmed role back to storage for persistence.
  useEffect(() => {
    if (
      finalRole === "doctor" ||
      finalRole === "patient" ||
      finalRole === "admin" ||
      finalRole === "pharmacy" ||
      finalRole === "practice_team" ||
      finalRole === "super_admin" ||
      finalRole === "moh_admin" ||
      finalRole === "institution_admin" ||
      finalRole === "facility_admin"
    ) {
      writeLastRole(finalRole, auth?.user?.id);
    }
  }, [finalRole, auth?.user?.id]);

  // 1. STALL: If we are still fetching the profile from DB, don't redirect yet.
  // This stops a Doctor from accidentally being flashed to the Patient home.
  if (isAuthTruthResolving(auth)) return <RouteFallback label="Loading AfriMed..." />;

  if (auth?.user?.id && !profile?.onboarding_completed && !controlledFirstUse.resolved) {
    return <RouteFallback label="Preparing your AfriMed™ setup..." />;
  }

  if (controlledFirstUse.controlled && controlledFirstUse.journey === "research") {
    if (
      controlledFirstUse.onboardingCompleted &&
      controlledFirstUse.institutionMembership?.assigned &&
      controlledFirstUse.institutionMembership?.destination
    ) {
      return <Navigate to={controlledFirstUse.institutionMembership.destination} replace />;
    }
    if (
      controlledFirstUse.onboardingCompleted &&
      hasPasswordConfigured(auth?.user)
    ) {
      return <Navigate to="/settings/research" replace />;
    }
    const target = `/onboarding/research?first_use=1&stage=${encodeURIComponent(controlledFirstUse.stage || "research_basic_details")}`;
    return <Navigate to={target} replace />;
  }

  if (controlledFirstUse.controlled && controlledFirstUse.role) {
    const routeBase = selectControlledFirstUseRoute({
      role: controlledFirstUse.role,
      onboardingCompleted: false,
      campaignId: "server-owned",
    });
    const target = controlledFirstUse.stage
      ? `${routeBase}${routeBase.includes("?") ? "&" : "?"}stage=${encodeURIComponent(controlledFirstUse.stage)}`
      : routeBase;
    return <Navigate to={target} replace />;
  }

  if (!finalRole) {
    if (profile?.hasDoctor && profile?.hasPatient) {
      return <Navigate to="/choose-role" replace />;
    }
    return <Navigate to="/onboarding" replace />;
  }

  if (finalRole === "doctor" && !profile?.onboarding_completed) {
    return <Navigate to="/onboarding/doctor?role=doctor" replace />;
  }

  if (finalRole === "patient" && !profile?.onboarding_completed) {
    return <Navigate to="/onboarding/patient?role=patient" replace />;
  }

  if (finalRole === "pharmacy" && !profile?.onboarding_completed) {
    return <Navigate to="/onboarding/pharmacy" replace />;
  }

  if (settingsWelcomePending) {
    return <Navigate to={`/settings/${finalRole}`} replace />;
  }

  return (
    <Navigate
      to={
        finalRole === "doctor"
          ? "/home/doctor"
          : finalRole === "patient"
            ? "/home/patient"
            : finalRole === "pharmacy"
              ? "/pharmacy/dashboard"
              : finalRole === "practice_team"
                ? "/practice-team/welcome"
              : finalRole === "super_admin" ||
                  finalRole === "moh_admin" ||
                  finalRole === "institution_admin" ||
                  finalRole === "facility_admin"
                ? "/institutions/home"
            : "/admin/kpi"
      }
      replace
    />
  );
}

function RequireCompletedRoleHome({ role, children }) {
  const auth = useAuth() || {};
  const profile = auth?.profile || null;

  if (isAuthTruthResolving(auth)) {
    return <RouteFallback />;
  }

  if (role === "doctor" && !profile?.onboarding_completed) {
    return <Navigate to="/onboarding/doctor?role=doctor" replace />;
  }

  if (role === "patient" && !profile?.onboarding_completed) {
    return <Navigate to="/onboarding/patient?role=patient" replace />;
  }

  if (role === "pharmacy" && !profile?.onboarding_completed) {
    return <Navigate to="/onboarding/pharmacy" replace />;
  }

  return children;
}

function PharmacyMemberOnboardingRedirect({ children }) {
  const auth = useAuth() || {};
  const { pathname, search } = useLocation();
  const isPharmacyOnboardingEdit =
    pathname.startsWith("/onboarding/pharmacy") &&
    new URLSearchParams(search || "").get("edit") === "1";
  const [state, setState] = React.useState({
    checking: Boolean(auth?.user?.id),
    redirect: false,
  });

  useEffect(() => {
    let alive = true;
    const userId = String(auth?.user?.id || "").trim();
    const email = String(auth?.user?.email || auth?.profile?.email || "").trim().toLowerCase();

    if (isPharmacyOnboardingEdit) {
      setState({ checking: false, redirect: false });
      return () => {
        alive = false;
      };
    }

    if (!userId) {
      setState({ checking: false, redirect: false });
      return () => {
        alive = false;
      };
    }

    setState({ checking: true, redirect: false });

    async function resolveMemberAccess() {
      try {
        const access = await resolvePersistedPharmacyAccess({ userId, email });
        const memberAccess = getActivePharmacyMemberAccess(access, { userId, email });
        if (!alive) return;
        if (memberAccess) {
          writeLastRole("pharmacy", userId);
          setState({ checking: false, redirect: true });
          return;
        }
      } catch (error) {
        console.warn("[Routes] pharmacy member onboarding guard skipped:", error?.message || error);
      }
      if (alive) {
        setState({ checking: false, redirect: false });
      }
    }

    resolveMemberAccess();
    return () => {
      alive = false;
    };
  }, [auth?.profile?.email, auth?.user?.email, auth?.user?.id, isPharmacyOnboardingEdit]);

  if (isAuthTruthResolving(auth) || state.checking) {
    // This guard wraps the shared onboarding router; it must not name the
    // pharmacy lane while an MoH, clinician, or patient journey is resolving.
    return <RouteFallback label="Preparing onboarding..." />;
  }

  if (state.redirect) {
    return <Navigate to="/pharmacy/dashboard" replace state={{ pharmacyMember: true }} />;
  }

  return children;
}

/**
 * Optional: persist role whenever we are on role-specific spaces.
 * This makes “neutral routes” stable even if opened after a refresh.
 */
function RoleTracker() {
  const auth = useAuth() || {};
  const { pathname } = useLocation();
  useEffect(() => {
    if (
      pathname.startsWith("/home/doctor") ||
      pathname.startsWith("/settings/doctor") ||
      pathname.startsWith("/profile-settings/doctor") ||
      pathname.startsWith("/profile/doctor")
    ) {
      writeLastRole("doctor", auth?.user?.id);
    } else if (pathname.startsWith("/pharmacy")) {
      writeLastRole("pharmacy", auth?.user?.id);
    } else if (pathname.startsWith("/institutions")) {
      const role = resolveRole(auth, { allowStorage: false });
      writeLastRole(role || "institution_admin", auth?.user?.id);
    } else if (
      pathname.startsWith("/home/patient") ||
      pathname.startsWith("/settings/patient") ||
      pathname.startsWith("/profile-settings/patient") ||
      pathname.startsWith("/profile/patient")
    ) {
      writeLastRole("patient", auth?.user?.id);
    }
  }, [pathname, auth?.user?.id]);

  return null;
}

function resolveTrackedActivity(pathname, auth) {
  const role = resolveRole(auth, { allowStorage: true });
  const path = String(pathname || "").trim();
  if (!path) return null;

  const routes = [
    { match: "/practice-team", route: "/practice-team", role: "practice_team" },
    { match: "/home/patient", route: "/home/patient", role: "patient" },
    { match: "/appointments/doctors", route: "/appointments/doctors", role: "patient" },
    { match: "/appointments/doctor", route: "/appointments/doctor", role: "patient" },
    { match: "/appointments/book", route: "/appointments/book", role: "patient" },
    { match: "/appointments/confirm", route: "/appointments/confirm", role: "patient" },
    { match: "/appointments/pay", route: "/appointments/pay", role: "patient" },
    { match: "/messages", route: "/messages", role: role || "" },
    { match: "/settings/patient/p/ehr", route: "/settings/patient/p/ehr", role: "patient" },
    { match: "/profile-settings/patient/p/ehr", route: "/profile-settings/patient/p/ehr", role: "patient" },
    { match: "/settings/patient", route: "/settings/patient", role: "patient" },
    { match: "/profile-settings/patient", route: "/profile-settings/patient", role: "patient" },
    { match: "/settings/doctor", route: "/settings/doctor", role: "doctor" },
    { match: "/profile-settings/doctor", route: "/profile-settings/doctor", role: "doctor" },
    { match: "/profile/doctor", route: "/profile/doctor", role: "patient" },
    { match: "/prescriptions", route: "/prescriptions", role: "patient" },
  ];

  const hit = routes.find((entry) => path.startsWith(entry.match));
  if (!hit) return null;
  if (!hit.role) return null;
  return {
    role: hit.role || role || "",
    route: hit.route,
    eventType: "route_visit",
  };
}

function ActivityTracker() {
  const auth = useAuth() || {};
  const { pathname } = useLocation();
  const lastSentRef = React.useRef({ key: "", at: 0 });

  useEffect(() => {
    const userId = String(auth?.user?.id || "").trim();
    if (!userId) return;

    const activity = resolveTrackedActivity(pathname, auth);
    if (!activity?.route) return;

    const now = Date.now();
    const key = `${userId}:${activity.role}:${activity.route}`;
    if (lastSentRef.current.key === key && now - lastSentRef.current.at < 15000) {
      return;
    }
    lastSentRef.current = { key, at: now };

    void writeUserActivityEvent({
      userId,
      role: activity.role,
      route: activity.route,
      eventType: activity.eventType,
      metadata: {
        pathname,
      },
    }).catch((error) => {
      console.error("[activity-tracker] route write failed:", error?.message || error, {
        userId,
        route: activity.route,
        pathname,
      });
    });
  }, [auth, auth?.user?.id, pathname]);

  return null;
}

function RootEntryGate({ hasAuthCallbackPayload, callbackTarget }) {
  if (hasAuthCallbackPayload) return <Navigate to={callbackTarget} replace />;
  return (
    <Suspense fallback={<RouteFallback label="Loading login..." />}>
      <LoginPage />
    </Suspense>
  );
}

function AdminEntryGate() {
  const auth = useAuth() || {};
  const role = normalizeRole(auth?.profile?.role || auth?.role);
  const target =
    role === "support"
      ? "/admin/support"
      : role === "kyc_reviewer"
        ? "/admin/kyc"
        : "/admin/kpi";

  if (auth?.loading && !auth?.user?.id) {
    return <RouteFallback label="Checking admin session..." />;
  }

  if (!auth?.user?.id) {
    return (
      <Suspense fallback={<RouteFallback label="Loading admin sign-in..." />}>
        <AdminSignIn />
      </Suspense>
    );
  }

  return (
    <ProtectedRoute allowed={["super_admin", "admin", "support", "kyc_reviewer"]} loginPath="/admin">
      <Navigate to={target} replace />
    </ProtectedRoute>
  );
}

function isCreatePasswordPromptSuppressedPath(pathname) {
  const path = String(pathname || "").toLowerCase();
  if (!path) return true;
  return (
    path === "/" ||
    path.startsWith("/login") ||
    path.startsWith("/auth/callback") ||
    path.startsWith("/callback") ||
    path.startsWith("/forgot-password") ||
    path.startsWith("/reset-password") ||
    // Controlled first-use owns password creation itself. A delayed global
    // prompt here races that stage and can bounce an incomplete account
    // through the completed-settings guard before the form can be used.
    path.startsWith("/onboarding") ||
    path.startsWith("/choose-role") ||
    path.startsWith("/settings/research/account/create-password") ||
    path.includes("/login-security/create-password") ||
    path.includes("/login-security/change-password")
  );
}

function ResearchPasswordFirstUseRoute() {
  const auth = useAuth() || {};
  const [disposition, setDisposition] = React.useState({ resolved: false, result: null });
  const pendingRoute = "/onboarding/research?first_use=1&stage=membership_pending";

  React.useEffect(() => {
    let alive = true;
    if (!auth?.user?.id) return () => { alive = false; };
    setDisposition({ resolved: false, result: null });
    resolveStagingFirstUseDisposition()
      .then((result) => {
        if (alive) setDisposition({ resolved: true, result });
      })
      .catch(() => {
        if (alive) setDisposition({ resolved: true, result: null });
      });
    return () => { alive = false; };
  }, [auth?.user?.id]);

  if (auth?.loading || !disposition.resolved) {
    return <RouteFallback label="Preparing your AfriMed™ account security..." />;
  }

  const result = disposition.result;
  const isEligibleResearchFirstUse =
    result?.controlled === true &&
    result?.journey === "research" &&
    result?.onboardingCompleted === true;
  if (!isEligibleResearchFirstUse) return <Navigate to="/home" replace />;

  if (hasPasswordConfigured(auth?.user)) {
    const destination =
      result?.institutionMembership?.assigned && result?.institutionMembership?.destination
        ? result.institutionMembership.destination
        : "/settings/research";
    return <Navigate to={destination} replace />;
  }

  return (
    <ChangePassword
      intent="create"
      firstUseReturnTo={pendingRoute}
      firstUseNextStage=""
      firstUseAdvance={false}
      firstUseLoginNext="/settings/research"
      loginSecurityPathOverride="/settings/research/account"
    />
  );
}

function resolveCreatePasswordBasePath(auth, pathname) {
  if (String(pathname || "").startsWith("/settings/research/account")) {
    return "/settings/research/account";
  }
  const role = resolveRole(auth);
  if (role === "doctor" || String(pathname || "").startsWith("/settings/doctor")) {
    return "/settings/doctor/login-security";
  }
  if (role === "patient" || String(pathname || "").startsWith("/settings/patient")) {
    return "/settings/patient/login-security";
  }
  return "";
}

function isCreatePasswordPromptReady(pathname) {
  if (typeof document === "undefined") return true;
  const path = String(pathname || "").toLowerCase();
  if (!path.startsWith("/settings")) return true;
  const bodyText = document.body?.innerText || "";
  return /\bSettings\b/.test(bodyText) || /\bAccount & Security\b/.test(bodyText) || /\bCreate your password\b/.test(bodyText);
}

function CreatePasswordPrompt() {
  const auth = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);

  const user = auth?.user || null;
  const userId = String(user?.id || "").trim();
  const pathname = location.pathname || "";
  // Patient first use intentionally explains its sign-out-before-resume
  // behaviour before the password form. Clinician first use preserves its
  // session and is deliberately excluded from this warning.
  const patientFirstUsePasswordWarning =
    location.state?.patientFirstUsePasswordWarning === true &&
    pathname === "/settings/patient/login-security/create-password";
  const firstUseCoordinator = new URLSearchParams(location.search || "").get("first_use") === "1";
  const firstUseStage = new URLSearchParams(location.search || "").get("stage") || "";
  const basePath = resolveCreatePasswordBasePath(auth, pathname);
  const [cookieConsentReady, setCookieConsentReady] = React.useState(() =>
    readConsentState().decided === true,
  );
  const passwordSetMarker = `${user?.user_metadata?.password_set === true ? "1" : "0"}:${
    user?.user_metadata?.password_changed_at || ""
  }`;

  useEffect(() => {
    let delayTimer = null;
    let readyTimer = null;
    let readyAttempts = 0;

    if (!AUTH_PASSWORD_PRIMARY_ENABLED) {
      setOpen(false);
      return;
    }
    if (
      !userId ||
      auth?.loading ||
      (auth?.profileResolved === false && !basePath.includes("research"))
    ) {
      setOpen(false);
      return;
    }
    if (!basePath || (isCreatePasswordPromptSuppressedPath(pathname) && !patientFirstUsePasswordWarning)) {
      setOpen(false);
      return;
    }
    if (firstUseCoordinator && (!cookieConsentReady || firstUseStage !== "password")) {
      setOpen(false);
      return;
    }
    if (!shouldOfferCreatePassword(user)) {
      setOpen(false);
      return;
    }

    setOpen(false);
    const scheduleAfterReady = () => {
      if (!isCreatePasswordPromptReady(pathname) && readyAttempts < 30) {
        readyAttempts += 1;
        readyTimer = window.setTimeout(scheduleAfterReady, 100);
        return;
      }
      delayTimer = window.setTimeout(() => {
        setOpen(true);
      }, CREATE_PASSWORD_PROMPT_DELAY_MS);
    };

    readyTimer = window.setTimeout(scheduleAfterReady, 0);

    return () => {
      if (delayTimer) window.clearTimeout(delayTimer);
      if (readyTimer) window.clearTimeout(readyTimer);
    };
  }, [auth?.loading, auth?.profileResolved, basePath, cookieConsentReady, firstUseCoordinator, firstUseStage, location.search, location.state?.patientFirstUsePasswordWarning, pathname, passwordSetMarker, patientFirstUsePasswordWarning, user, userId]);

  React.useEffect(() => onConsentChanged((state) => setCookieConsentReady(state?.decided === true)), []);

  if (!AUTH_PASSWORD_PRIMARY_ENABLED || !userId || !basePath) return null;

  const returnTo = firstUseCoordinator
    ? basePath.includes("research")
      ? "/onboarding/research?first_use=1&stage=membership_pending"
      : `${basePath.includes("patient") ? "/onboarding/patient" : "/onboarding/doctor"}?first_use=1&stage=details`
    : `${location.pathname}${location.search || ""}${location.hash || ""}`;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/35" />
      <BasicDialogFrame
        role="dialog"
        aria-modal="true"
        title="Create your password"
        body={
          <span>
            You can now create a password for faster sign-in.
            <br />
            {patientFirstUsePasswordWarning ? (
              <>
                Your secure sign-in link will continue to work during staging.
                <br />
              </>
            ) : null}
            After creating your password, you’ll be signed out. Please sign back in with your new password.
          </span>
        }
        contentClassName="pt-7 pb-3 sm:pt-8 sm:pb-4"
        actionsClassName="pt-2 sm:pt-3"
      >
        <button
          className="mx-auto min-h-[48px] min-w-[180px] rounded-xl border px-8 py-3 text-label-large whitespace-nowrap transition-colors"
          style={{
            background: "rgba(29, 79, 215, 0.16)",
            color: "rgb(var(--clr-on-surface))",
            borderColor: "rgba(29, 79, 215, 0.40)",
            boxShadow: "0 3px 6px -3px rgba(29, 79, 215, 0.35)",
          }}
          onClick={() => {
            setOpen(false);
            navigate(`${basePath}/create-password${firstUseCoordinator ? "?first_use=1" : ""}`, {
              state: { returnTo, firstUse: firstUseCoordinator || location.state?.firstUse === true },
            });
          }}
        >
          Create password
        </button>
      </BasicDialogFrame>
    </div>
  );
}

export default function AppRoutes() {
  const location = useLocation();
  const providerFailure = socialFallbackFailurePath(`https://app.afrimed.tech${location.pathname}${location.search}${location.hash}`);
  if (providerFailure) return <Navigate to={providerFailure} replace />;
  const search = typeof window !== "undefined" ? window.location.search || "" : "";
  const hash = typeof window !== "undefined" ? window.location.hash || "" : "";
  const hasAuthCallbackPayload =
    search.includes("code=") ||
    search.includes("token_hash=") ||
    hash.includes("access_token=") ||
    hash.includes("refresh_token=") ||
    hash.includes("type=");
  const callbackTarget = `/auth/callback${search}${hash}`;

  return (
    <>
      <CreatePasswordPrompt />
      <Routes>
      {/* PUBLIC ---------------------------------------------------------------- */}
      <Route
        path="/"
        element={<RootEntryGate hasAuthCallbackPayload={hasAuthCallbackPayload} callbackTarget={callbackTarget} />}
      />

      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/r/:token" element={<ReferralDocumentRedirect />} />
      <Route path="/appointments/join/:invitationId" element={<AdultDependantBrowserJoin />} />
      <Route
        path="/invite/setup"
        element={
          <Suspense fallback={<RouteFallback label="Loading invite setup..." />}>
            <InviteSetupPage />
          </Suspense>
        }
      />
      <Route
        path="/signup"
        element={<StagingSignupPage />}
      />

      <Route
        path="/login"
        element={
          <Suspense
            fallback={
              <RouteFallback label="Loading login..." />
            }
          >
            {/* Also apply the check here for direct auth links that land on /login. */}
            {hasAuthCallbackPayload ? (
              <Navigate to={callbackTarget} replace />
            ) : (
              <LoginPage />
            )}
          </Suspense>
        }
      />

      <Route
        path="/auth/callback"
        element={
          <Suspense
            fallback={
              <RouteFallback
                label="Finishing sign-in..."
              />
            }
          >
            <AuthCallback />
          </Suspense>
        }
      />

      <Route
        path="/admin/auth/callback"
        element={
          <Suspense
            fallback={
              <RouteFallback
                label="Finishing admin sign-in..."
              />
            }
          >
            <AuthCallback />
          </Suspense>
        }
      />

      <Route path="/preferences" element={<CommunicationPreferencesPage />} />
      <Route path="/unsubscribe" element={<CommunicationUnsubscribePage />} />
      <Route path="/communications/preferences" element={<CommunicationPreferencesPage />} />
      <Route path="/communications/unsubscribe" element={<CommunicationUnsubscribePage />} />

      <Route
        path="/feedback"
        element={
          <Suspense fallback={<RouteFallback label="Loading feedback..." />}>
            <FeedbackPortal />
          </Suspense>
        }
      />
      <Route
        path="/feedback/thanks"
        element={
          <Suspense fallback={<RouteFallback label="Loading feedback..." />}>
            <FeedbackThanks />
          </Suspense>
        }
      />

      <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/terms" element={<TermsConditionsPage />} />
      <Route path="/legal/data-deletion" element={<DataDeletionPage />} />
      <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
      <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
      <Route path="/data-deletion" element={<Navigate to="/legal/data-deletion" replace />} />

      {/* Back-compat for older links that still target /callback?code=... */}
      <Route
        path="/callback"
        element={
          <Suspense
            fallback={
              <RouteFallback
                label="Finishing sign-in..."
              />
            }
          >
            <AuthCallback />
          </Suspense>
        }
      />

      {/* ONBOARDING ----------------------------------------------------------- */}
      <Route
        path="/onboarding/*"
        element={
          <Suspense
            fallback={
              <RouteFallback
                label="Loading onboarding..."
              />
            }
          >
            <PharmacyMemberOnboardingRedirect>
              <OnboardingRouter />
            </PharmacyMemberOnboardingRedirect>
          </Suspense>
        }
      />

      {/* The server-governed onboarding router owns the single StepCategory implementation. */}
      <Route
        path="/onboarding/category"
        element={<Navigate to="/onboarding?first_use=1&stage=category" replace />}
      />

      <Route
        path="/pharmacy"
        element={<PharmacyEntry />}
      />
      <Route
        path="/pharmacy/invite"
        element={<PharmacyInviteLanding />}
      />
      <Route
        path="/practice-team/invite"
        element={<PracticeTeamInviteLanding />}
      />
      <Route
        path="/practice-team/accept"
        element={<PracticeTeamInviteAccept />}
      />
      <Route
        path="/practice-team/welcome"
        element={<PracticeTeamWorkspaceRoute><PracticeTeamWelcome /></PracticeTeamWorkspaceRoute>}
      />
      <Route
        path="/practice-team/tasks"
        element={<PracticeTeamWorkspaceRoute requiredSectionId="tasks"><PracticeTeamTasks /></PracticeTeamWorkspaceRoute>}
      />
      <Route
        path="/practice-team/appointments"
        element={<PracticeTeamWorkspaceRoute requiredSectionId="appointments"><PracticeTeamAppointments /></PracticeTeamWorkspaceRoute>}
      />
      <Route
        path="/practice-team/messages"
        element={<PracticeTeamWorkspaceRoute requiredSectionId="messages"><PracticeTeamMessages /></PracticeTeamWorkspaceRoute>}
      />
      <Route
        path="/practice-team/patients"
        element={<PracticeTeamWorkspaceRoute requiredSectionId="patients"><PracticeTeamPatients /></PracticeTeamWorkspaceRoute>}
      />
      <Route
        path="/practice-team/account"
        element={<PracticeTeamWorkspaceRoute><PracticeTeamAccount /></PracticeTeamWorkspaceRoute>}
      />
      <Route
        path="/practice-team/account/appearance"
        element={<PracticeTeamWorkspaceRoute><PracticeTeamAppearance /></PracticeTeamWorkspaceRoute>}
      />
      <Route
        path="/pharmacy/dashboard"
        element={
          <PharmacyProtectedRoute>
            <PharmacyWorkspace section="dashboard" />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/inventory"
        element={
          <PharmacyProtectedRoute>
            <PharmacyWorkspace section="inventory" />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/inventory/new"
        element={
          <PharmacyProtectedRoute>
            <PharmacyInventoryItemCreatePage />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/inventory/:itemId/edit"
        element={
          <PharmacyProtectedRoute>
            <PharmacyInventoryItemCreatePage />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/prescriptions"
        element={
          <PharmacyProtectedRoute>
            <PharmacyWorkspace section="prescriptions" />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/prescriptions/scan"
        element={
          <PharmacyProtectedRoute>
            <PharmacyScanPrescription />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/scan-prescription"
        element={<Navigate to="/pharmacy/prescriptions/scan" replace />}
      />
      <Route
        path="/pharmacy/orders/:prescriptionId"
        element={
          <PharmacyProtectedRoute>
            <PharmacyOrderDetails />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/payments"
        element={
          <PharmacyProtectedRoute>
            <PharmacyWorkspace section="payments" />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/messages"
        element={
          <PharmacyProtectedRoute>
            <PharmacyMessagesShell>
              <MessagesPage />
            </PharmacyMessagesShell>
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/messages/:conversationId"
        element={
          <PharmacyProtectedRoute>
            <PharmacyMessagesShell>
              <ConversationPage />
            </PharmacyMessagesShell>
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/settings"
        element={
          <PharmacyProtectedRoute>
            <PharmacyWorkspace section="settings" />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/account"
        element={
          <PharmacyProtectedRoute>
            <PharmacyAccount />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/settings/appearance"
        element={
          <PharmacyProtectedRoute>
            <PharmacyAppearance />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/settings/members/new"
        element={
          <PharmacyProtectedRoute>
            <PharmacyMemberCreatePage />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/help"
        element={
          <PharmacyProtectedRoute>
            <HelpCentre
              audienceOverride="pharmacy"
              basePath="/pharmacy/help"
              pharmacyShell
            />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/help/:categorySlug"
        element={
          <PharmacyProtectedRoute>
            <HelpCentre
              audienceOverride="pharmacy"
              basePath="/pharmacy/help"
              pharmacyShell
            />
          </PharmacyProtectedRoute>
        }
      />
      <Route
        path="/pharmacy/help/:categorySlug/:articleSlug"
        element={
          <PharmacyProtectedRoute>
            <HelpCentre
              audienceOverride="pharmacy"
              basePath="/pharmacy/help"
              pharmacyShell
            />
          </PharmacyProtectedRoute>
        }
      />

      <Route
        path="/settings/doctor/p/stripe-connect-launch"
        element={
          <Suspense fallback={<RouteFallback label="Opening payout settings…" />}>
            <StripeConnectLaunch />
          </Suspense>
        }
      />

      <Route
        path="/settings/patient/p/ehr/reset-password/confirm"
        element={<EhrPasswordResetConfirm />}
      />
      <Route
        path="/settings/patient/login-security/change-password/confirm"
        element={<ChangePassword />}
      />
      <Route
        path="/settings/doctor/login-security/change-password/confirm"
        element={<ChangePassword />}
      />

      <Route
        path="/research"
        element={
          <ProtectedRoute loginPath="/login" requireProfile={false}>
            <Navigate to="/settings/research" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/research"
        element={
          <ProtectedRoute loginPath="/login" requireProfile={false}>
            <Suspense fallback={<RouteFallback label="Loading Research • Public health profile..." />}>
              <ResearchPublicHealthSettings />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/research/account"
        element={
          <ProtectedRoute loginPath="/login" requireProfile={false}>
            <ResearchPasswordFirstUseRoute />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/research/account/create-password"
        element={
          <ProtectedRoute loginPath="/login" requireProfile={false}>
            <ResearchPasswordFirstUseRoute />
          </ProtectedRoute>
        }
      />

      <Route path="/pay/sponsor/:opaqueToken" element={<SponsorWorldlinePayment />} />

      {/* Scoped investigators have no product-wide Institution role.
          These exact pages perform server-backed active-assignment checks. */}
      <Route
        element={
          <ProtectedRoute loginPath="/login" requireProfile={false}>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/institutions/follow-ups" element={<InstitutionsPage />} />
        <Route path="/institutions/institutions/follow-ups" element={<LegacyInstitutionFollowupRedirect />} />
        <Route path="/institutions/alerts" element={<InstitutionsPage />} />
        <Route path="/institutions/regional-health" element={<InstitutionsPage />} />
      </Route>

      {/* AUTHED APP (guard + shell) ------------------------------------------- */}
      <Route
        element={
          <ProtectedRoute
            allowed={[
              "patient",
              "doctor",
              "pharmacy",
              "practice_team",
              "super_admin",
              "moh_admin",
              "institution_admin",
              "facility_admin",
            ]}
          >
            <AppLayout />
          </ProtectedRoute>
        }
      >

        <Route path="/choose-role" element={<ChooseRole />} />

        {/* SINGLE, CLEAN /home ------------------------------------------------ */}
        <Route path="/home" element={<HomeGate />} />

        <Route
          path="/doctor/preferences/*"
          element={<LegacyPreferencesRedirect role="doctor" />}
        />
        <Route
          path="/patient/preferences/*"
          element={<LegacyPreferencesRedirect role="patient" />}
        />

        {/* Role-specific homes ------------------------------------------------ */}
        <Route
          path="/home/patient"
          element={
            <RequireRole allow={["patient"]}>
              <PatientHome />
            </RequireRole>
          }
        />

        <Route
          path="/patient/photos"
          element={
            <RequireRole allow={["patient"]}>
              <PatientPhotos />
            </RequireRole>
          }
        />

        {/* ✅ Top-level redirect (NOT nested under /appointments) */}
        <Route path="/doctors" element={<Navigate to="/appointments/doctors" replace />} />

        <Route
          path="/appointments"
          element={
            <AppointmentsFiltersProvider>
              <AppointmentsLayout />
            </AppointmentsFiltersProvider>
          }
        >
          <Route index element={<AppointmentsIndex />} />

          <Route path="doctors" element={<AppointmentsDoctorsPage />} />
          <Route path="hospitals" element={<AppointmentsHospitalsPage />} />
          <Route path="pharmacies" element={<AppointmentsPharmaciesPage />} />

          {/* Booking flow */}
          <Route path="doctor/:id" element={<AppointmentDoctorPreview />} />
          <Route path="book/:id" element={<AppointmentBook />} />

          {/* Canonical payment/checkout routes */}
          <Route path="payment/worldline/return" element={<WorldlinePaymentReturn />} />
          <Route path="payment" element={<AppointmentPay />} />
          <Route path="payment/:id" element={<AppointmentPay />} />
          <Route path="checkout" element={<AppointmentConfirm />} />
          <Route path="checkout/:id" element={<AppointmentConfirm />} />

          {/* Legacy aliases (kept for compatibility) */}
          <Route path="pay/:id" element={<AppointmentPay />} />
          <Route path="pay" element={<AppointmentPay />} />
          <Route path="confirm/:id" element={<AppointmentConfirm />} />
          <Route path="confirm" element={<AppointmentConfirm />} />

          {/* Details + edit */}
          <Route path="details/:id" element={<AppointmentDetails />} />
          <Route path="call/:id" element={<AppointmentCall />} />
          <Route path="video/:id" element={<AppointmentVideo />} />
          <Route path="summary/:id" element={<AppointmentSummary />} />
          <Route path="prescriptions" element={<PatientPrescriptionsIndex />} />
          <Route path="prescriptions/:id" element={<PatientPrescriptionDetails />} />
          <Route path="prescriptions/:id/pay" element={<PatientPrescriptionPayment />} />
          <Route
            path="prescriptions/new/:appointmentId"
            element={
              <RequireRole allow={["doctor"]}>
                <RequireCompletedRoleHome role="doctor">
                  <DoctorPrescriptionWizard />
                </RequireCompletedRoleHome>
              </RequireRole>
            }
          />
          <Route path="details/:id/edit" element={<AppointmentEditMenu />} />
          <Route path="edit/:id" element={<EditAppointment />} />
          <Route path="edit-location/:id" element={<EditAppointmentLocation />} />
          <Route path="nearby" element={<AppointmentsNearby />} />
          <Route path="past" element={<PastAppointments />} />
          <Route path="past/:id" element={<PastAppointmentDetails />} />

          <Route path="success" element={<AppointmentSuccess />} />
        </Route>

        <Route path="/verify/rx/:id" element={<VerifyPrescription />} />
        <Route path="/pharmacies/scan-prescription" element={<Navigate to="/pharmacy/prescriptions/scan" replace />} />

        <Route
          path="/home/doctor"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorHome />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        {/* ✅ Doctor Appointments (doctor-side) */}
        <Route
          path="/doctors/appointments"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorAppointmentsPage />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/appointments/:appointmentId"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorAppointmentDetails />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/appointments/:appointmentId/patient-profile"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <ClinicianAppointmentPatientProfile />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/appointments/:appointmentId/diagnosis"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorDiagnosisNotes />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/appointments/:appointmentId/ehr-request"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorEhrRequest />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/appointments/:appointmentId/vitalrep-beta"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorVitalRepBeta />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/referral-network"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorReferralNetwork />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/appointments/:appointmentId/refer"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorReferralCreate />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/patient/referrals/:referralId"
          element={
            <RequireRole allow={["patient", "doctor"]}>
              <PatientReferralDetails />
            </RequireRole>
          }
        />

        <Route
          path="/doctors/patients/:patientId/ehr"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorPatientEhr />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/patients/:patientId/ehr/past-appointments"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorPastAppointments />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/patients/:patientId/ehr/past-appointments/:appointmentId"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorPastAppointmentDetails />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctors/appointments/:appointmentId/patientphotos"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorPatientPhotos />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/doctor/dashboard"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorDashboardPage />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route path="/institutions/evaluation/accept" element={<ProtectedRoute loginPath="/login" requireProfile={false}><InstitutionEvaluationAccept /></ProtectedRoute>} />
        <Route path="/institutions/clinician-invitations/:invitationId" element={<ProtectedRoute loginPath="/login" requireProfile={false}><InstitutionClinicianInvitationReview /></ProtectedRoute>} />
        <Route path="/institutions/surveillance-invitations/accept" element={<ProtectedRoute loginPath="/login" requireProfile={false}><InstitutionScopedInvitationAccept /></ProtectedRoute>} />
        <Route path="/institutes/*" element={<LegacyInstitutesRedirect />} />
        <Route
          path="/institutions/*"
          element={
            <ProtectedRoute loginPath="/login" requireProfile={false}><InstitutionsPage /></ProtectedRoute>
          }
        />

        <Route
          path="/doctors/prescriptions"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <DoctorRecentPrescriptionsPage />
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/messages"
          element={
            <RequireRole allow={["patient", "doctor", "pharmacy"]}>
              <MessagesPage />
            </RequireRole>
          }
        />

        <Route
          path="/messages/:conversationId"
          element={
            <RequireRole allow={["patient", "doctor", "pharmacy"]}>
              <ConversationPage />
            </RequireRole>
          }
        />

        <Route
          path="/help"
          element={
            <RequireRole allow={["patient", "doctor", "pharmacy"]}>
              <HelpCentre />
            </RequireRole>
          }
        />
        <Route
          path="/help/:categorySlug"
          element={
            <RequireRole allow={["patient", "doctor", "pharmacy"]}>
              <HelpCentre />
            </RequireRole>
          }
        />
        <Route
          path="/help/:categorySlug/:articleSlug"
          element={
            <RequireRole allow={["patient", "doctor", "pharmacy"]}>
              <HelpCentre />
            </RequireRole>
          }
        />

        <Route
          path="/help-support"
          element={
            <RequireRole allow={["patient", "doctor", "pharmacy"]}>
              <HelpSupport />
            </RequireRole>
          }
        />
        <Route
          path="/knowledge-base"
          element={
            <RequireRole allow={["patient", "doctor"]}>
              <KnowledgeBase />
            </RequireRole>
          }
        />
        <Route
          path="/knowledge-base/article/:slug"
          element={
            <RequireRole allow={["patient", "doctor"]}>
              <KnowledgeBaseArticleView />
            </RequireRole>
          }
        />

        {/* FAQ now lands in the guided Help Centre foundation. */}
        <Route path="/legal/faq" element={<Navigate to="/help" replace />} />

        {/* Doctor-only: Regional Health -------------------------------------- */}
        <Route
          path="/regional-health"
          element={
            <RequireRole allow={["doctor"]}>
              <RegionalHealth />
            </RequireRole>
          }
        />

        {/* Directory pages (shared for now) ---------------------------------- */}
        <Route
          path="/hospitals"
          element={
            <AppointmentsFiltersProvider>
              <HospitalsPage />
            </AppointmentsFiltersProvider>
          }
        />
        <Route path="/pharmacies" element={<PharmaciesPage />} />
        <Route path="/ambulances" element={<AmbulancesPage />} />

        {/* Public-ish profiles (inside authed shell per your setup) ----------- */}
        <Route
          path="/patient/profile"
          element={
            <ProfileRoleBoundary requestedRole="patient">
              <Suspense
                fallback={
                    <RouteFallback
                    label="Loading patient profile..."
                  />
                }
              >
                <PatientProfile />
              </Suspense>
            </ProfileRoleBoundary>
          }
        />

        <Route
          path="/doctor/profile"
          element={
            <ProfileRoleBoundary requestedRole="doctor">
              <Suspense
                fallback={
                    <RouteFallback
                    label="Loading doctor profile..."
                  />
                }
              >
                <DoctorProfile />
              </Suspense>
            </ProfileRoleBoundary>
          }
        />

        <Route
          path="/doctor/p/availability"
          element={<Navigate to="/settings/doctor/p/availability" replace />}
        />

        {/* Old /profile entry (settings-style profile) ----------------------- */}
        <Route
          path="/profile/*"
          element={
            <Suspense
              fallback={
                <RouteFallback label="Loading profile..." />
              }
            >
              <ProfileSettings />
            </Suspense>
          }
        />

        <Route
          path="/profile-settings/patient/notifications/:id"
          element={
            <Suspense
              fallback={
                  <RouteFallback
                  label="Loading notification..."
                />
              }
            >
              <NotificationsOpen />
            </Suspense>
          }
        />

        <Route
          path="/settings/patient/notifications/:id"
          element={
            <Suspense
              fallback={
                  <RouteFallback
                  label="Loading notification..."
                />
              }
            >
              <NotificationsOpen />
            </Suspense>
          }
        />

        <Route
          path="/settings/doctor/notifications/:id"
        element={
          <Suspense
            fallback={
                  <RouteFallback
                  label="Loading notification..."
                />
              }
            >
              <DoctorNotificationsOpen />
            </Suspense>
          }
        />

        {/* Keep the clinician Bell target independent from the broader settings
            route tree. The list and detail must use the same role guard. */}
        <Route
          path="/settings/doctor/notifications"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <Suspense
                  fallback={
                    <RouteFallback
                      label="Loading notifications..."
                      i18nKey="routeFallback.loadingNotification"
                    />
                  }
                >
                  <DoctorNotifications />
                </Suspense>
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        {/* Role-aware settings main entry ------------------------------------ */}
        <Route path="/settings" element={<SettingsGate />} />
        <Route path="/settings/*" element={<SettingsScopedRedirect />} />

        {/* Explicit patient / doctor settings shortcuts ---------------------- */}
        <Route
          path="/settings/patient/*"
          element={
            <RequireRole allow={["patient"]}>
              <RequireCompletedRoleHome role="patient">
                <Suspense
                  fallback={
                      <RouteFallback
                      label="Loading patient settings..."
                      i18nKey="routeFallback.loadingPatientSettings"
                    />
                  }
                >
                  <PatientSettingsRoutes />
                </Suspense>
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        <Route
          path="/settings/doctor/*"
          element={
            <RequireRole allow={["doctor"]}>
              <RequireCompletedRoleHome role="doctor">
                <Suspense
                  fallback={
                      <RouteFallback
                      label="Loading doctor settings..."
                      i18nKey="routeFallback.loadingDoctorSettings"
                    />
                  }
                >
                  <DoctorSettingsRoutes />
                </Suspense>
              </RequireCompletedRoleHome>
            </RequireRole>
          }
        />

        {/* add more authed routes here: /appointments, /messages, etc. ------- */}
      </Route>

      {/* ADMIN ---------------------------------------------------------------- */}
      <Route
        path="/admin"
        element={<AdminEntryGate />}
      />

      <Route
        path="/admin/kpi"
        element={
          <ProtectedRoute allowed={["super_admin", "admin"]} loginPath="/admin">
            <Suspense
              fallback={<RouteFallback label="Loading AfriMed KPI..." />}
            >
              <AdminKpi />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/kpi/invite-funnel"
        element={
          <ProtectedRoute allowed={["super_admin", "admin"]} loginPath="/admin">
            <Suspense
              fallback={<RouteFallback label="Loading invite funnel..." />}
            >
              <AdminInviteFunnel />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/account"
        element={
          <ProtectedRoute allowed={["super_admin", "admin", "support", "kyc_reviewer"]} loginPath="/admin">
            <Suspense
              fallback={<RouteFallback label="Loading admin account..." />}
            >
              <AdminAccount />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/kyc"
        element={
          <ProtectedRoute allowed={["super_admin", "admin", "kyc_reviewer"]} loginPath="/admin">
            <Suspense
              fallback={<RouteFallback label="Loading KYC..." />}
            >
              <AdminKyc />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/feedback"
        element={
          <ProtectedRoute allowed={["super_admin", "admin", "support"]} loginPath="/admin">
            <Suspense
              fallback={<RouteFallback label="Loading feedback..." />}
            >
              <AdminFeedback />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/support"
        element={
          <ProtectedRoute allowed={["super_admin", "admin", "support"]} loginPath="/admin">
            <Suspense
              fallback={<RouteFallback label="Loading support tickets..." />}
            >
              <AdminSupport />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/regional-health-surveillance"
        element={
          <Suspense
            fallback={<RouteFallback label="Resolving Regional Health workspace..." />}
          >
            <RegionalHealthLegacyRedirect />
          </Suspense>
        }
      />

      <Route
        path="/admin/users"
        element={
          <ProtectedRoute allowed={["super_admin", "admin", "support"]} loginPath="/admin">
            <Suspense
              fallback={<RouteFallback label="Loading staging users..." />}
            >
              <AdminUsers />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/team"
        element={
          <ProtectedRoute allowed={["super_admin"]} loginPath="/admin">
            <Suspense
              fallback={<RouteFallback label="Loading admin team..." />}
            >
              <AdminTeam />
            </Suspense>
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/team/accept"
        element={
          <ProtectedRoute loginPath="/login" requireProfile={false}>
            <AdminTeamAccept />
          </ProtectedRoute>
        }
      />

      {/* CATCH-ALL ------------------------------------------------------------ */}
      <Route path="*" element={<DefaultGate />} />
      </Routes>
    </>
  );
}

function DefaultGate() {
  const { user, loading, BYPASS } = useAuth();
  if (loading) return <RouteFallback label="Loading AfriMed..." />;

  // Dev shortcut follows the same completed-user landing as normal auth.
  if (BYPASS) return <Navigate to="/home" replace />;

  // Normal users land on Home (HomeGate will route correctly)
  return user ? <Navigate to="/home" replace /> : <Navigate to="/login" replace />;
}
